import BigNumber from 'bignumber.js';
import { Numeral } from 'numeral';

import {
  EOrderBalanceType,
  EOrderDealDirection,
  EOrderExpiry,
  EOrderIndicate,
  EOrderState,
  EOrderStateCentroid,
  EOrderStatus,
  EOrderType,
  TOrderFillPolicy
} from '@webtrader/core/api/order';
import { EOrderTableColumn } from '@webtrader/core/api/order/columnPreference';
import { ETradeFillPolicy } from '@webtrader/core/api/trade';
import {
  EBuySellType,
  EOrderProfitStatus,
  EOrderTypeLabel,
  LIMIT_ORDER_TYPES,
  MARKET_ORDER_TYPES,
  STOP_LIMIT_ORDER_TYPES,
  STOP_ORDER_TYPES
} from '@webtrader/core/constants/order';
import i18n from '@webtrader/core/i18n';
import { EBulkCloseType } from '@webtrader/core/services/analysis/constants';

interface IMapOrderStatusWithTypeToTextParams {
  status: EOrderStatus;
  orderType: EOrderType;
  orderBalanceType?: EOrderBalanceType;
}

type TMapOrderTableColumnToTextValue = (pendingOrder?: boolean) => string;
type TOrderTypeTextMapValue = (suffix?: boolean) => string;

export const shouldDisplayProfit = (status: EOrderStatus) => {
  switch (status) {
    case EOrderStatus.CLOSED:
      return true;
    default:
      break;
  }

  return false;
};

export const numeralToNumber = (num: Numeral) => {
  const result = num.value();

  if (result === null) {
    return undefined;
  }

  return result;
};

export const checkIsOpenOrder = (state: EOrderState) => {
  switch (state) {
    case EOrderState.OPEN_NORMAL:
    case EOrderState.OPEN_REMAND:
    case EOrderState.OPEN_RESTORED:
      return true;
    default:
      return false;
  }
};

export const checkIsOpenPosition = (orderType?: EOrderType) => {
  switch (orderType) {
    case EOrderType.BUY:
    case EOrderType.SELL:
      return true;
    default:
      return false;
  }
};

export const getOrderBuySellType = (orderType: EOrderType) => {
  switch (orderType) {
    case EOrderType.BUY:
    case EOrderType.BUY_LIMIT:
    case EOrderType.BUY_STOP:
    case EOrderType.BUY_STOP_LIMIT:
      return EBuySellType.BUY;
    default:
      return EBuySellType.SELL;
  }
};

export const mapPriceByOrderType = (ask: number, bid: number, orderType: EOrderType) => {
  switch (orderType) {
    case EOrderType.BUY:
    case EOrderType.BUY_LIMIT:
    case EOrderType.BUY_STOP:
    case EOrderType.BUY_STOP_LIMIT:
      return bid;
    case EOrderType.SELL:
    case EOrderType.SELL_LIMIT:
    case EOrderType.SELL_STOP:
    case EOrderType.SELL_STOP_LIMIT:
      return ask;
    default:
      return undefined;
  }
};

export const mapOrderTypeToOrderTypeLabel = (orderType: EOrderType | undefined) => {
  if (orderType !== undefined) {
    if (MARKET_ORDER_TYPES.includes(orderType)) {
      return EOrderTypeLabel.MARKET;
    }
    if (LIMIT_ORDER_TYPES.includes(orderType)) {
      return EOrderTypeLabel.LIMIT;
    }
    if (STOP_ORDER_TYPES.includes(orderType)) {
      return EOrderTypeLabel.STOP;
    }
    if (STOP_LIMIT_ORDER_TYPES.includes(orderType)) {
      return EOrderTypeLabel.STOP_LIMIT;
    }
  }
  return undefined;
};

const orderTypeTextMap: Record<EOrderType, TOrderTypeTextMapValue> = {
  [EOrderType.BUY]: (suffix?: boolean) => (suffix ? i18n.t('Buy Order') : i18n.t('Buy')),
  [EOrderType.SELL]: (suffix?: boolean) => (suffix ? i18n.t('Sell Order') : i18n.t('Sell')),
  [EOrderType.BUY_LIMIT]: (suffix?: boolean) => (suffix ? i18n.t('Buy Limit Order') : i18n.t('Buy Limit')),
  [EOrderType.SELL_LIMIT]: (suffix?: boolean) => (suffix ? i18n.t('Sell Limit Order') : i18n.t('Sell Limit')),
  [EOrderType.BUY_STOP]: (suffix?: boolean) => (suffix ? i18n.t('Buy Stop Order') : i18n.t('Buy Stop')),
  [EOrderType.SELL_STOP]: (suffix?: boolean) => (suffix ? i18n.t('Sell Stop Order') : i18n.t('Sell Stop')),
  [EOrderType.BALANCE]: () => i18n.t('Balance'),
  [EOrderType.CREDIT]: () => i18n.t('Credit'),
  [EOrderType.BUY_STOP_LIMIT]: (suffix?: boolean) =>
    suffix ? i18n.t('Buy Stop Limit Order') : i18n.t('Buy Stop Limit'),
  [EOrderType.SELL_STOP_LIMIT]: (suffix?: boolean) =>
    suffix ? i18n.t('Sell Stop Limit Order') : i18n.t('Sell Stop Limit'),
  [EOrderType.CLOSE_BY]: () => i18n.t('Close By')
};

const orderStateTextMap: Record<EOrderState, () => string> = {
  [EOrderState.OPEN_NORMAL]: () => i18n.t('Open Normal'),
  [EOrderState.OPEN_REMAND]: () => i18n.t('Open Remand'),
  [EOrderState.OPEN_RESTORED]: () => i18n.t('Open Restored'),
  [EOrderState.CLOSED_NORMAL]: () => i18n.t('Close Normal'),
  [EOrderState.CLOSED_PART]: () => i18n.t('Closed Partial'),
  [EOrderState.CLOSED_BY]: () => i18n.t('Closed By'),
  [EOrderState.DELETED]: () => i18n.t('Deleted'),
  [EOrderState.STARTED]: () => i18n.t('Started'),
  [EOrderState.PLACED]: () => i18n.t('Placed'),
  [EOrderState.CANCELED]: () => i18n.t('Canceled'),
  [EOrderState.PARTIAL]: () => i18n.t('Partially Filled'),
  [EOrderState.FILLED]: () => i18n.t('Filled'),
  [EOrderState.REJECTED]: () => i18n.t('Rejected'),
  [EOrderState.EXPIRED]: () => i18n.t('Expired'),
  [EOrderState.REQUEST_ADD]: () => i18n.t('Request Add'),
  [EOrderState.REQUEST_MODIFY]: () => i18n.t('Request Modify'),
  [EOrderState.REQUEST_CANCEL]: () => i18n.t('Request Cancel')
};

const orderStateTextMapCentroid: Record<EOrderStateCentroid, () => string> = {
  [EOrderStateCentroid.CREATED]: () => i18n.t('Created'),
  [EOrderStateCentroid.NEW]: () => i18n.t('New'),
  [EOrderStateCentroid.FILLED]: () => i18n.t('Filled'),
  [EOrderStateCentroid.PARTIALLY_FILLED]: () => i18n.t('Partially Filled'),
  [EOrderStateCentroid.REJECTED]: () => i18n.t('Rejected'),
  [EOrderStateCentroid.CANCELED]: () => i18n.t('Canceled')
};

const orderDealDirectionTextMap: Record<EOrderDealDirection, () => string> = {
  [EOrderDealDirection.IN]: () => i18n.t('In'),
  [EOrderDealDirection.OUT]: () => i18n.t('Out'),
  [EOrderDealDirection.INOUT]: () => i18n.t('In/Out'),
  [EOrderDealDirection.OUT_BY]: () => i18n.t('Out By')
};

export const mapOrderTypeToText = (type: EOrderType, suffix?: boolean) => {
  return orderTypeTextMap[type]?.(suffix).toUpperCase() || type.toString().toUpperCase();
};

export const getOrderTypeTextForChart = (type: EOrderType) => orderTypeTextMap[type]?.() || type.toString();

export const mapOrderStateToText = (state: EOrderState) => {
  return {
    orderStateText: orderStateTextMap[state]?.() || state.toString()
  };
};

export const mapOrderStateToTextCentroid = (state: EOrderStateCentroid) => {
  return {
    orderStateText: orderStateTextMapCentroid[state]?.() || state?.toString()
  };
};

export const mapDealDirectionToText = (direction: EOrderDealDirection) => {
  return {
    dealDirectionText: orderDealDirectionTextMap[direction]?.().toUpperCase() || direction.toString().toUpperCase()
  };
};

export const mapCloseOrderTypeToText = (type: EOrderType, isClose = true) => {
  const actionText = isClose ? i18n.t('closed') : i18n.t('deleted');
  switch (type) {
    case EOrderType.BUY:
      return i18n.t('Buy order {{actionText}} successfully.', { actionText });
    case EOrderType.BUY_STOP:
      return i18n.t('Buy Stop order {{actionText}} successfully.', { actionText });
    case EOrderType.BUY_LIMIT:
      return i18n.t('Buy Limit order {{actionText}} successfully.', { actionText });
    case EOrderType.BUY_STOP_LIMIT:
      return i18n.t('Buy Stop Limit order {{actionText}} successfully.', { actionText });
    case EOrderType.SELL:
      return i18n.t('Sell order {{actionText}} successfully.', { actionText });
    case EOrderType.SELL_STOP:
      return i18n.t('Sell Stop order {{actionText}} successfully.', { actionText });
    case EOrderType.SELL_LIMIT:
      return i18n.t('Sell Limit order {{actionText}} successfully.', { actionText });
    case EOrderType.SELL_STOP_LIMIT:
      return i18n.t('Sell Stop Limit order {{actionText}} successfully.', { actionText });
    default:
      return '';
  }
};

export const mapConfirmCloseOrderToText = (symbol: string, type: EOrderType, isClose = true) => {
  const actionText = isClose ? i18n.t('closing') : i18n.t('deleting');
  switch (type) {
    case EOrderType.BUY:
      return i18n.t('Would you like to confirm {{actionText}} {{symbol}} Buy order?', { actionText, symbol });
    case EOrderType.BUY_STOP:
      return i18n.t('Would you like to confirm {{actionText}} {{symbol}} Buy Stop order?', { actionText, symbol });
    case EOrderType.BUY_LIMIT:
      return i18n.t('Would you like to confirm {{actionText}} {{symbol}} Buy Limit order?', { actionText, symbol });
    case EOrderType.BUY_STOP_LIMIT:
      return i18n.t('Would you like to confirm {{actionText}} {{symbol}} Buy Stop Limit order?', {
        actionText,
        symbol
      });
    case EOrderType.SELL:
      return i18n.t('Would you like to confirm {{actionText}} {{symbol}} Sell order?', { actionText, symbol });
    case EOrderType.SELL_STOP:
      return i18n.t('Would you like to confirm {{actionText}} {{symbol}} Sell Stop order?', { actionText, symbol });
    case EOrderType.SELL_LIMIT:
      return i18n.t('Would you like to confirm {{actionText}} {{symbol}} Sell Limit order?', { actionText, symbol });
    case EOrderType.SELL_STOP_LIMIT:
      return i18n.t('Would you like to confirm {{actionText}} {{symbol}} Sell Stop Limit order?', {
        actionText,
        symbol
      });
    default:
      return '';
  }
};

export const mapOrderStatusToText = (status: EOrderStatus) => {
  switch (status) {
    case EOrderStatus.CLOSED:
      return i18n.t('Closed');
    case EOrderStatus.DELETED:
      return i18n.t('Cancelled');
    case EOrderStatus.EXPIRED:
      return i18n.t('Expired');
    case EOrderStatus.CREDITED:
      return i18n.t('Credited');
    default:
      return status;
  }
};

export const mapOrderBalanceTypeToText = (type: EOrderBalanceType) => {
  switch (type) {
    case EOrderBalanceType.OTHERS:
      return i18n.t('Others');
    case EOrderBalanceType.TOP_UP:
      return i18n.t('Topup');
    case EOrderBalanceType.DEPOSIT:
      return i18n.t('Deposit');
    case EOrderBalanceType.WITHDRAWAL:
      return i18n.t('Withdrawal');
    case EOrderBalanceType.PENALTY_FEE:
      return i18n.t('Penalty');
    case EOrderBalanceType.UPFRONT_FEE:
      return i18n.t('Upfront');
    case EOrderBalanceType.SUBSCRIPTION:
      return i18n.t('Subscription');
    case EOrderBalanceType.UNSUBSCRIPTION:
      return i18n.t('Unsubscription');
    case EOrderBalanceType.MANAGEMENT_FEE:
      return i18n.t('Management');
    case EOrderBalanceType.PERFORMANCE_FEE:
      return i18n.t('Performance');
    default:
      return type as string;
  }
};

export const mapOrderStatusWithTypeToText = (params: IMapOrderStatusWithTypeToTextParams) => {
  const { status, orderType, orderBalanceType } = params;

  if (orderType === EOrderType.BALANCE && orderBalanceType !== undefined) {
    return mapOrderBalanceTypeToText(orderBalanceType);
  }

  return mapOrderStatusToText(status);
};

export const mapOrderExpiryToText = (tradeExpiry: EOrderExpiry) => {
  switch (tradeExpiry) {
    case EOrderExpiry.GTC:
      return i18n.t('GTC');
    case EOrderExpiry.GTD:
      return i18n.t('GTD');
    case EOrderExpiry.SPECIFIED_TIMESTAMP:
      return i18n.t('SPECIFIED');
    case EOrderExpiry.SPECIFIED_DAY:
      return i18n.t('SPECIFIED DAY');
    default:
      return '';
  }
};

export const mapOrderFillPolicyToText = (orderFillPolicy: TOrderFillPolicy) => {
  switch (orderFillPolicy) {
    case ETradeFillPolicy.FOK:
      return i18n.t('Fill or Kill');
    case ETradeFillPolicy.IOC:
      return i18n.t('Immediate or Cancel');
    case ETradeFillPolicy.RETURN:
      return i18n.t('Return');
    case ETradeFillPolicy.GTC:
      return i18n.t('Good Till Cancelled');
    default:
      return '';
  }
};

export const mapOrderIndicateToText = (indicate: EOrderIndicate) => {
  switch (indicate) {
    case EOrderIndicate.POINTS:
      return i18n.t('POINTS');
    case EOrderIndicate.PRICE:
      return i18n.t('PRICE');
    default:
      return '';
  }
};

export const mapOrderTypeLabelToText = (label: EOrderTypeLabel) => {
  switch (label) {
    case EOrderTypeLabel.LIMIT:
      return i18n.t('Limit');
    case EOrderTypeLabel.MARKET:
      return i18n.t('Market');
    case EOrderTypeLabel.STOP_LIMIT:
      return i18n.t('Stop Limit');
    default:
      return '';
  }
};

export const getSelectorOptionText = (option: EOrderExpiry | EOrderIndicate | EOrderTypeLabel) => {
  switch (option) {
    case EOrderExpiry.GTC:
    case EOrderExpiry.GTD:
    case EOrderExpiry.SPECIFIED_TIMESTAMP:
    case EOrderExpiry.SPECIFIED_DAY:
      return mapOrderExpiryToText(option);
    case EOrderIndicate.POINTS:
    case EOrderIndicate.PRICE:
      return mapOrderIndicateToText(option);
    case EOrderTypeLabel.LIMIT:
    case EOrderTypeLabel.MARKET:
    case EOrderTypeLabel.STOP_LIMIT:
      return mapOrderTypeLabelToText(option);
    default:
      return '';
  }
};

export const mapOrderTableColumnToText: Record<EOrderTableColumn, TMapOrderTableColumnToTextValue> = {
  [EOrderTableColumn.VOLUME]: () => i18n.t('Volume'),
  [EOrderTableColumn.UNITS]: () => i18n.t('Units'),
  [EOrderTableColumn.ENTRY_PRICE]: (pendingOrder?: boolean) => (pendingOrder ? i18n.t('Price') : i18n.t('Entry Price')),
  [EOrderTableColumn.CURRENT_PRICE]: () => i18n.t('Current Price'),
  [EOrderTableColumn.TAKE_PROFIT]: () => i18n.t('Take Profit'),
  [EOrderTableColumn.STOP_LOSS]: () => i18n.t('Stop Loss'),
  [EOrderTableColumn.SWAP]: () => i18n.t('Swap'),
  [EOrderTableColumn.FILL_POLICY]: () => i18n.t('Fill Policy'),
  [EOrderTableColumn.EXPIRY]: () => i18n.t('Expiry'),
  [EOrderTableColumn.EXPIRY_DATE]: () => i18n.t('Expiry Date'),
  [EOrderTableColumn.PENDING_PRICE]: () => i18n.t('Stop Limit Price'),
  [EOrderTableColumn.CLOSE_PRICE]: () => i18n.t('Close Price'),
  [EOrderTableColumn.COMMISSION]: () => i18n.t('Commission'),
  [EOrderTableColumn.REMARKS]: () => i18n.t('Remarks'),
  [EOrderTableColumn.STATE]: () => i18n.t('State'),
  [EOrderTableColumn.SIZE]: () => i18n.t('Size'),
  [EOrderTableColumn.FILL_VOLUME]: () => i18n.t('Fill Volume'),
  [EOrderTableColumn.VALIDITY]: () => i18n.t('Validity'),
  [EOrderTableColumn.DEVIATION]: () => i18n.t('Deviation'),
  [EOrderTableColumn.NOTIONAL]: () => i18n.t('Notional'),
  [EOrderTableColumn.AVG_FILLED_PRICE]: () => i18n.t('Avg Filled Price'),
  [EOrderTableColumn.PRICE]: () => i18n.t('Price'),
  [EOrderTableColumn.CURRENCY]: () => i18n.t('Currency'),
  [EOrderTableColumn.CENTROID_OPEN_ORDER]: () => i18n.t('Open Centroid Order Id'),
  [EOrderTableColumn.CENTROID_CLOSE_ORDER]: () => i18n.t('Close Centroid Order Id'),
  [EOrderTableColumn.CLOSE_DATE]: () => i18n.t('Close Date')
};

export const mapAlertMessageTitleNewOrder = (orderTypeLabel?: EOrderTypeLabel) => {
  if (orderTypeLabel === EOrderTypeLabel.STOP) {
    return i18n.t('Stop Order Submitted');
  } else if (orderTypeLabel === EOrderTypeLabel.STOP_LIMIT) {
    return i18n.t('Stop Limit Order Submitted');
  } else if (orderTypeLabel === EOrderTypeLabel.LIMIT) {
    return i18n.t('Limit Order Submitted');
  } else {
    return i18n.t('Market Order Submitted');
  }
};

export const mapAlertMessageTitleUpdateOrder = (orderTypeLabel?: EOrderTypeLabel) => {
  if (orderTypeLabel === EOrderTypeLabel.STOP) {
    return i18n.t('Stop Order Updated');
  } else if (orderTypeLabel === EOrderTypeLabel.STOP_LIMIT) {
    return i18n.t('Stop Limit Order Updated');
  } else if (orderTypeLabel === EOrderTypeLabel.LIMIT) {
    return i18n.t('Limit Order Updated');
  } else {
    return i18n.t('Market Order Updated');
  }
};

export const mapOrderProfitStatusToBulkCloseType = (status: EOrderProfitStatus) => {
  switch (status) {
    case EOrderProfitStatus.PROFIT:
      return EBulkCloseType.PROFITABLE_POSITIONS;
    case EOrderProfitStatus.LOSS:
      return EBulkCloseType.LOSING_POSITIONS;
    default:
      return EBulkCloseType.ALL_POSITIONS;
  }
};

export const convertVolumeToUnits = (
  volume: number | undefined,
  contractSize: number | undefined,
  lotSizeStep: number | undefined
): string => {
  if (!contractSize || !lotSizeStep || !volume) {
    return volume?.toFixed(lotSizeStep ?? 0) ?? '';
  }
  const minUnitsBN = new BigNumber(lotSizeStep).times(contractSize);
  const unitsBN = new BigNumber(volume).times(contractSize);
  return trimTrailingZero(unitsBN.toFixed(minUnitsBN.decimalPlaces() || 0));
};

export const getUnitsMinValue = (minContractLotSize: number | undefined, contractSize: number | undefined): string => {
  if (!minContractLotSize || !contractSize) {
    return '0';
  }
  const lotSizeStepUnitsBN = new BigNumber(minContractLotSize).times(contractSize);

  return trimTrailingZero(
    new BigNumber(minContractLotSize).times(contractSize).toFixed(lotSizeStepUnitsBN.decimalPlaces() ?? 0)
  );
};

/**
 * To remove trailing zero of the number string , eg: 10.10 to 10.1
 * */
export const trimTrailingZero = (value: string | undefined): string => {
  if (!value) {
    return '';
  }
  // Remove commas from the string before parsing
  const cleanValue = value.replace(/,/g, '');
  if (isNaN(Number(cleanValue))) {
    return '';
  }
  return parseFloat(cleanValue).toString();
};
